import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Lightbulb, Link2, Sparkles, Loader2, Trash2, Send, Upload,
  Copy, CheckCircle2, ImageIcon, ExternalLink, Wand2, Heart, MessageCircle,
  Bookmark, Pencil, X, Check, Instagram, RefreshCw, Calendar as CalendarIcon,
  Video, Play,
} from "lucide-react";

const TAG_CLS = "text-[10px] tracking-[0.3em] uppercase text-[#A69076]";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const proxyImg = (url) => {
  if (!url) return "";
  // weserv.nl is a free public image proxy — bypasses IG CDN hotlink protection.
  // Encode the URL WITHOUT the scheme (weserv convention).
  const clean = url.replace(/^https?:\/\//i, "");
  return `https://images.weserv.nl/?url=${encodeURIComponent(clean)}&default=data:image/svg+xml,%3Csvg width%3D%27400%27 height%3D%27400%27 xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%3E%3Crect fill%3D%27%23F4F0EB%27 width%3D%27400%27 height%3D%27400%27%2F%3E%3C%2Fsvg%3E`;
};

export default function InspirationPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");  // all | not_recreated | recreated
  // Analyze form
  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchUrls, setBatchUrls] = useState("");
  const [batchBusy, setBatchBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCaption, setManualCaption] = useState("");
  const [manualImage, setManualImage] = useState("");
  const [manualMediaType, setManualMediaType] = useState("image");
  const [manualVideoFile, setManualVideoFile] = useState(null);
  const [manualVideoUrl, setManualVideoUrl] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  // Recreation controls (per open item)
  const [openItem, setOpenItem] = useState(null);
  const [fidelity, setFidelity] = useState(50);
  const [service, setService] = useState("generico");
  const [targetMedia, setTargetMedia] = useState("");
  const [includeDoctora, setIncludeDoctora] = useState(false);
  const [doctoraPhotos, setDoctoraPhotos] = useState([]);
  const [uploadingDoctora, setUploadingDoctora] = useState(false);
  const [recreating, setRecreating] = useState(false);
  const [publishingId, setPublishingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const path = filter === "all" ? "/inspiration/items" : `/inspiration/trending?status=${filter}`;
      const r = await api.get(path);
      const data = filter === "all" ? (r.data || []) : (r.data?.items || []);
      setItems(data);
    } catch (e) {
      toast.error("Error cargando inspiraciones");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  // Load doctora reference photos once
  useEffect(() => {
    api.get("/brand/doctora-photos")
      .then((r) => setDoctoraPhotos(r.data || []))
      .catch(() => { /* optional */ });
  }, []);

  const uploadDoctoraPhoto = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Máximo 15 MB"); return; }
    setUploadingDoctora(true);
    try {
      const b64 = await fileToDataUrl(file);
      await api.post("/brand/doctora-photos", { image_base64: b64, label: file.name.slice(0, 60) });
      const r = await api.get("/brand/doctora-photos");
      setDoctoraPhotos(r.data || []);
      toast.success("Foto de referencia guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo");
    } finally { setUploadingDoctora(false); }
  };

  const deleteDoctoraPhoto = async (id) => {
    if (!confirm("¿Eliminar esta foto de referencia?")) return;
    try {
      await api.delete(`/brand/doctora-photos/${id}`);
      const r = await api.get("/brand/doctora-photos");
      setDoctoraPhotos(r.data || []);
      toast.success("Eliminada");
    } catch { toast.error("Error"); }
  };

  const batchAnalyze = async () => {
    const list = batchUrls
      .split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error("Pega al menos un URL"); return; }
    setBatchBusy(true);
    try {
      const r = await api.post("/inspiration/batch-analyze", { urls: list, max_parallel: 3 });
      const { unique_new, succeeded, failed, skipped_existing } = r.data;
      toast.success(`Lote analizado · ${succeeded} nuevas · ${skipped_existing} ya existían · ${failed} fallaron`, { duration: 8000 });
      setBatchOpen(false); setBatchUrls("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en batch");
    } finally { setBatchBusy(false); }
  };

  const resetManual = () => {
    setManualCaption(""); setManualImage("");
    if (manualVideoUrl) URL.revokeObjectURL(manualVideoUrl);
    setManualVideoFile(null); setManualVideoUrl(""); setUploadPct(0);
  };

  // Poll an uploaded-video item until its deep analysis finishes (max ~5 min).
  const pollAnalysis = async (itemId) => {
    for (let i = 0; i < 100; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const r = await api.get(`/inspiration/items/${itemId}`);
        const st = r.data?.analysis_status;
        if (st !== "analyzing") {
          await load();
          if (st === "done") {
            toast.success("Análisis del video listo ✨");
            setOpenItem(r.data);
            setFidelity(50); setService("generico"); setTargetMedia("");
          } else {
            toast.error("El análisis del video falló — puedes recrear pero con menos contexto");
          }
          return;
        }
      } catch { /* keep polling */ }
    }
  };

  const analyze = async (useManual = false) => {
    if (!url && !useManual) { toast.error("Pega la URL de Instagram"); return; }
    if (useManual && !manualImage && !manualVideoFile) { toast.error("Sube una captura del post o el video del reel"); return; }
    setAnalyzing(true);
    try {
      if (useManual && manualVideoFile) {
        // VIDEO path: multipart upload → deep frame-by-frame analysis in background
        const fd = new FormData();
        fd.append("file", manualVideoFile);
        fd.append("ig_url", url || "");
        fd.append("caption", manualCaption || "");
        const r = await api.post("/inspiration/analyze-video", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => setUploadPct(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0),
        });
        toast.success("Video subido — la IA está estudiando el reel completo (30-90s)…", { duration: 8000 });
        setUrl(""); setManualOpen(false); resetManual();
        await load();
        pollAnalysis(r.data.id);
        return;
      }
      const body = { ig_url: url };
      if (useManual) {
        body.manual_image_base64 = manualImage;
        body.manual_caption = manualCaption;
        body.manual_media_type = manualMediaType;
      }
      const r = await api.post("/inspiration/analyze", body);
      toast.success("Post analizado ✨");
      setUrl(""); setManualOpen(false);
      resetManual();
      await load();
      // Auto-open the newly-analyzed item so the user can jump straight to recreate.
      setOpenItem(r.data);
      setFidelity(50); setService("generico"); setTargetMedia("");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error analizando";
      toast.error(detail, { duration: 8000 });
      if (String(detail).toLowerCase().includes("bloqueó") || String(detail).toLowerCase().includes("blocked")) {
        setManualOpen(true);
      }
    } finally { setAnalyzing(false); }
  };

  // Progress messages during long recreation (reel = 2-3 min due to Sora 2)
  const [recreateStep, setRecreateStep] = useState("");

  const recreate = async () => {
    if (!openItem) return;
    setRecreating(true);
    const effectiveMedia = targetMedia
      || (openItem.media_type === "video" ? "reel"
          : openItem.media_type === "carousel" ? "carousel" : "post");
    setRecreateStep(
      effectiveMedia === "reel" ? "Generando concepto…"
      : effectiveMedia === "carousel" ? "Generando 4 slides con Nano Banana (30-45s)…"
      : "Generando imagen editorial (15-25s)…"
    );
    try {
      const body = {
        inspiration_id: openItem.id,
        fidelity, service_tag: service,
      };
      if (targetMedia) body.target_media = targetMedia;
      if (targetMedia === "reel" || effectiveMedia === "reel") body.include_doctora = includeDoctora;
      const r = await api.post("/inspiration/recreate", body);
      const newRec = r.data?.recreation;
      // Auto-fire Sora 2 for reels so user sees the final video without extra clicks
      if (newRec && newRec.media_type === "reel" && newRec.reel_prompt) {
        setRecreateStep("Enviando prompt a Sora 2…");
        try {
          await api.post(
            `/inspiration/items/${openItem.id}/recreations/${newRec.id}/generate-video`
          );  // returns immediately with status=generating
          // Poll for completion (up to 8 minutes)
          setRecreateStep("Generando video con Sora 2 (2-3 min)…");
          await pollVideoStatus(openItem.id, newRec.id, (status, elapsed) => {
            setRecreateStep(`Sora 2 · ${status === "generating" ? `procesando… (${Math.floor(elapsed/1000)}s)` : status}`);
          });
        } catch (ve) {
          toast.error(ve?.response?.data?.detail || "Video Sora 2 falló — puedes reintentar desde la card");
        }
      }
      toast.success("Recreación lista 🎨");
      const detail = await api.get(`/inspiration/items/${openItem.id}`);
      setOpenItem(detail.data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error recreando");
    } finally {
      setRecreating(false);
      setRecreateStep("");
    }
  };

  // Poll inspiration item until the given recreation.video_status is 'done' or 'failed'.
  const pollVideoStatus = async (itemId, recId, onTick) => {
    const start = Date.now();
    const maxMs = 8 * 60 * 1000;  // 8 min hard cap
    while (Date.now() - start < maxMs) {
      await new Promise((res) => setTimeout(res, 8000));  // 8s between polls
      try {
        const r = await api.get(`/inspiration/items/${itemId}`);
        const rec = (r.data?.recreations || []).find((x) => x.id === recId);
        const status = rec?.video_status;
        onTick && onTick(status || "generating", Date.now() - start);
        if (status === "done") return rec;
        if (status === "failed") {
          throw new Error(rec?.video_error || "Sora 2 falló");
        }
      } catch (e) {
        if (e.message && e.message.includes("Sora 2 falló")) throw e;
        // ignore transient poll errors and keep trying
      }
    }
    throw new Error("Timeout esperando a Sora 2 (8 min)");
  };

  const publishRecreation = async (rec) => {
    if (!openItem) return;
    const dtLocal = prompt(
      "Fecha y hora de publicación (opcional).\nDeja vacío para guardar como BORRADOR sin agenda.\n\nFormato: 2026-02-15 09:00",
      ""
    );
    // prompt returns null when user cancels
    if (dtLocal === null) return;
    let scheduled_for = null;
    if (dtLocal.trim()) {
      const parsed = new Date(dtLocal.replace(" ", "T"));
      if (isNaN(parsed.getTime())) { toast.error("Fecha inválida"); return; }
      scheduled_for = parsed.toISOString();
    }
    setPublishingId(rec.id);
    try {
      const r = await api.post(`/inspiration/items/${openItem.id}/publish`, {
        recreation_id: rec.id,
        scheduled_for,
      });
      const kindLabel = r.data.kind === "reel" ? "Reels" : "Cronograma";
      toast.success(scheduled_for
        ? `Programado en ${kindLabel} para ${new Date(scheduled_for).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}`
        : `Guardado como borrador en ${kindLabel}`);
      const detail = await api.get(`/inspiration/items/${openItem.id}`);
      setOpenItem(detail.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error publicando");
    } finally { setPublishingId(null); }
  };

  const del = async (id) => {
    if (!confirm("¿Eliminar esta inspiración y todas sus recreaciones?")) return;
    try {
      await api.delete(`/inspiration/items/${id}`);
      toast.success("Eliminado");
      load();
      if (openItem?.id === id) setOpenItem(null);
    } catch (e) {
      toast.error("Error eliminando");
    }
  };

  const _fidelityLabel = (v) => v >= 80 ? "Casi clon" : v >= 50 ? "Balanceado" : "Solo esencia";

  return (
    <div className="space-y-6" data-testid="inspiration-panel">
      {/* Input card */}
      <div className="bg-gradient-to-br from-[#1A1C1A] to-[#2a2b29] text-[#F4F0EB] rounded-2xl border border-[#1A1C1A] p-8">
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div>
            <div className={TAG_CLS + " mb-1"}>
              <Lightbulb className="w-3 h-3 inline mr-1.5 -mt-0.5" /> Inspiración · Recrear
            </div>
            <h3 className="font-display text-3xl leading-tight">Pega un link de Instagram<br />y recréalo con tu estilo</h3>
            <p className="text-[11px] text-[#A69076] mt-2 max-w-md leading-relaxed">
              Analizamos concepto + hook + estética. Después decides qué tanto respetar el original con el slider.
            </p>
          </div>
          <Wand2 className="w-7 h-7 text-[#D97757] flex-shrink-0" />
        </div>

        <div className="flex gap-2 flex-wrap">
          <div className="flex-1 min-w-[280px] relative">
            <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A69076]" />
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.instagram.com/p/... · /reel/... · /tv/..."
              className="pl-9 bg-[#2a2b29] border-[#3a3b39] text-[#F4F0EB] placeholder:text-[#5C5E5C] h-11"
              data-testid="inspiration-url-input"
              onKeyDown={(e) => { if (e.key === "Enter") analyze(false); }}
            />
          </div>
          <Button
            onClick={() => analyze(false)}
            disabled={analyzing || !url}
            className="bg-[#D97757] hover:bg-[#C66749] text-white h-11 px-6 gap-2"
            data-testid="inspiration-analyze-btn"
          >
            {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analizando…</> : <><Sparkles className="w-4 h-4" /> Analizar</>}
          </Button>
          <Button
            onClick={() => setBatchOpen(true)}
            variant="ghost"
            className="text-[#A69076] hover:text-[#F4F0EB] hover:bg-[#3a3b39] h-11"
            data-testid="inspiration-batch-toggle"
          >
            <Sparkles className="w-4 h-4 mr-1.5" /> Lote de URLs
          </Button>
          <Button
            onClick={() => setManualOpen(true)}
            variant="ghost"
            className="text-[#A69076] hover:text-[#F4F0EB] hover:bg-[#3a3b39] h-11"
            data-testid="inspiration-manual-toggle"
          >
            <Upload className="w-4 h-4 mr-1.5" /> Subir manualmente
          </Button>
        </div>
      </div>

      {/* Batch analyze dialog */}
      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent className="max-w-lg" data-testid="inspiration-batch-dialog">
          <DialogHeader>
            <DialogTitle>Lote de URLs de Instagram</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#5C5E5C] leading-relaxed">
              Pega hasta 20 URLs (una por línea o separadas por coma). Analizamos las que sean nuevas en paralelo — las que ya guardaste antes se saltan.
            </p>
            <Textarea
              value={batchUrls}
              onChange={(e) => setBatchUrls(e.target.value)}
              rows={10}
              placeholder="https://www.instagram.com/p/ABC.../\nhttps://www.instagram.com/reel/XYZ.../\n..."
              className="text-xs font-mono"
              data-testid="inspiration-batch-textarea"
            />
            <div className="text-[10px] text-[#A69076]">
              {batchUrls.split(/[\n,]+/).filter(Boolean).length} URLs detectadas
            </div>
            <Button
              onClick={batchAnalyze}
              disabled={batchBusy}
              className="w-full bg-[#1A1C1A] hover:bg-[#A69076] text-white gap-2"
              data-testid="inspiration-batch-submit"
            >
              {batchBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Analizando lote…</> : <><Sparkles className="w-4 h-4" /> Analizar todos</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual upload dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-lg" data-testid="inspiration-manual-dialog">
          <DialogHeader>
            <DialogTitle>Subir contenido manualmente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#5C5E5C] leading-relaxed">
              Instagram bloquea la lectura automática de posts. Sube tú misma el contenido:
              para <strong>reels</strong>, descarga el video (con fastdl.app o snapins.ai) y súbelo —
              la IA estudia el video completo: hook, ritmo, textos en pantalla y escenas.
            </p>
            <div>
              <Label className={TAG_CLS + " mb-1.5 block"}>URL (opcional)</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.instagram.com/p/..." />
            </div>
            <div>
              <Label className={TAG_CLS + " mb-1.5 block"}>Imagen o video del post</Label>
              <label className="block cursor-pointer border-2 border-dashed border-[#E6E2DC] rounded-lg p-6 text-center hover:border-[#D97757] transition">
                {manualVideoUrl ? (
                  <video src={manualVideoUrl} controls playsInline className="max-h-52 mx-auto rounded bg-black" data-testid="inspiration-manual-video-preview" />
                ) : manualImage ? (
                  <img src={manualImage} alt="" className="max-h-40 mx-auto rounded" />
                ) : (
                  <div className="text-xs text-[#A69076]">
                    <ImageIcon className="w-6 h-6 mx-auto mb-1" /> Click para subir captura o video del reel
                    <span className="block text-[10px] text-[#5C5E5C] mt-1">JPG · PNG · MP4 · MOV (máx 120 MB)</span>
                  </div>
                )}
                <input
                  type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden"
                  data-testid="inspiration-manual-file"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    if (f.type.startsWith("video/")) {
                      if (f.size > 120 * 1024 * 1024) { toast.error("Video muy grande (máx 120 MB)"); return; }
                      if (manualVideoUrl) URL.revokeObjectURL(manualVideoUrl);
                      setManualVideoFile(f); setManualVideoUrl(URL.createObjectURL(f));
                      setManualImage(""); setManualMediaType("video");
                    } else {
                      setManualImage(await fileToDataUrl(f));
                      if (manualVideoUrl) URL.revokeObjectURL(manualVideoUrl);
                      setManualVideoFile(null); setManualVideoUrl("");
                    }
                  }}
                />
              </label>
              {manualVideoFile && (
                <div className="flex items-center justify-between mt-1.5 text-[10px] text-[#5C5E5C]">
                  <span className="truncate max-w-[60%]">{manualVideoFile.name} · {(manualVideoFile.size / (1024 * 1024)).toFixed(1)} MB</span>
                  <button
                    type="button"
                    className="text-[#965D5D] hover:underline"
                    onClick={() => { URL.revokeObjectURL(manualVideoUrl); setManualVideoFile(null); setManualVideoUrl(""); }}
                  >Quitar video</button>
                </div>
              )}
            </div>
            <div>
              <Label className={TAG_CLS + " mb-1.5 block"}>Caption original</Label>
              <Textarea value={manualCaption} onChange={(e) => setManualCaption(e.target.value)} rows={4} placeholder="Pega el texto del post..." />
            </div>
            {!manualVideoFile && (
              <div>
                <Label className={TAG_CLS + " mb-1.5 block"}>Tipo</Label>
                <Select value={manualMediaType} onValueChange={setManualMediaType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Post (imagen única)</SelectItem>
                    <SelectItem value="carousel">Carrusel</SelectItem>
                    <SelectItem value="video">Reel / Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              onClick={() => analyze(true)}
              disabled={analyzing || (!manualImage && !manualVideoFile)}
              className="w-full bg-[#1A1C1A] hover:bg-[#A69076] text-white"
              data-testid="inspiration-manual-analyze"
            >
              {analyzing
                ? (manualVideoFile
                    ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {uploadPct < 100 ? `Subiendo video… ${uploadPct}%` : "Procesando…"}</>
                    : <Loader2 className="w-4 h-4 animate-spin" />)
                : (manualVideoFile ? "Subir y analizar video completo" : "Analizar")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery grid */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className={TAG_CLS}>Inspiraciones ({items.length})</div>
          <div className="flex items-center gap-1" data-testid="inspiration-filter-pills">
            {[
              { id: "all", label: "Todas" },
              { id: "not_recreated", label: "Sin recrear" },
              { id: "recreated", label: "Recreadas" },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full transition ${
                  f.id === filter
                    ? "bg-[#1A1C1A] text-white"
                    : "bg-[#F4F0EB] text-[#A69076] hover:bg-[#E6E2DC]"
                }`}
                data-testid={`inspiration-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#A69076] mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-sm text-[#A69076] italic py-8 text-center">
            Aún no hay inspiraciones. Pega un link de arriba para empezar.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="inspiration-grid">
            {items.map((it) => (
              <InspirationCard
                key={it.id}
                item={it}
                onOpen={() => {
                  setOpenItem(it);
                  setFidelity(50); setService("generico"); setTargetMedia("");
                }}
                onDelete={() => del(it.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail + recreate modal */}
      <Dialog open={!!openItem} onOpenChange={(v) => !v && setOpenItem(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="inspiration-detail-dialog">
          {openItem && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Recrear con tu estilo</DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-5">
                {/* Original */}
                <div>
                  <div className={TAG_CLS + " mb-2"}>Original</div>
                  {openItem.source_video_url ? (
                    <video
                      src={`${BACKEND}${openItem.source_video_url}`}
                      controls playsInline
                      className="w-full max-h-96 object-contain bg-black rounded-lg"
                      data-testid="inspiration-source-video"
                    />
                  ) : openItem.original_thumbnail_url ? (
                    <img
                      src={openItem.original_thumbnail_url}
                      referrerPolicy="no-referrer"
                      alt=""
                      className="w-full aspect-square object-cover rounded-lg bg-[#F4F0EB]"
                      onError={(e) => {
                        // Fallback to backend-cached thumb (if IG CDN blocked us).
                        const fallback = `${BACKEND}/api/inspiration/items/${openItem.id}/thumb`;
                        if (e.target.src !== fallback) e.target.src = fallback;
                        else e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="aspect-square rounded-lg bg-[#F4F0EB] flex items-center justify-center text-xs text-[#A69076]">
                      Sin miniatura
                    </div>
                  )}
                  <a href={openItem.ig_url} target="_blank" rel="noreferrer" className="text-[10px] text-[#D97757] hover:underline mt-2 inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Ver en Instagram
                  </a>
                  <div className="mt-3 space-y-2 text-xs">
                    <div>
                      <div className={TAG_CLS}>Concepto</div>
                      <p className="text-[#1A1C1A] mt-0.5">{openItem.analysis?.concept || "—"}</p>
                    </div>
                    <div>
                      <div className={TAG_CLS}>Hook</div>
                      <p className="text-[#1A1C1A] mt-0.5">{openItem.analysis?.hook || "—"}</p>
                    </div>
                    <div>
                      <div className={TAG_CLS}>Estilo visual</div>
                      <p className="text-[#5C5E5C] mt-0.5">{openItem.analysis?.visual_style || "—"}</p>
                    </div>
                    {openItem.original_caption && (
                      <details className="pt-1">
                        <summary className="text-[10px] tracking-[0.2em] uppercase text-[#A69076] cursor-pointer">Ver caption original</summary>
                        <p className="text-[#5C5E5C] whitespace-pre-wrap mt-1 leading-relaxed">{openItem.original_caption}</p>
                      </details>
                    )}
                  </div>
                </div>

                {/* Controls + recreations */}
                <div className="space-y-4">
                  <div>
                    <div className={TAG_CLS + " mb-2"}>Fidelidad al original: {fidelity}% · {_fidelityLabel(fidelity)}</div>
                    <input
                      type="range" min="0" max="100" step="5"
                      value={fidelity}
                      onChange={(e) => setFidelity(Number(e.target.value))}
                      className="w-full accent-[#D97757]"
                      data-testid="inspiration-fidelity-slider"
                    />
                    <div className="flex justify-between text-[9px] text-[#A69076] tracking-widest uppercase mt-1">
                      <span>Solo esencia</span><span>Balanceado</span><span>Casi clon</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className={TAG_CLS + " mb-1.5 block"}>Servicio</Label>
                      <Select value={service} onValueChange={setService}>
                        <SelectTrigger data-testid="inspiration-service-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="generico">Genérico</SelectItem>
                          <SelectItem value="estetica">Medicina Estética</SelectItem>
                          <SelectItem value="ortodoncia">Ortodoncia</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={TAG_CLS + " mb-1.5 block"}>Formato salida</Label>
                      <Select value={targetMedia || "auto"} onValueChange={(v) => setTargetMedia(v === "auto" ? "" : v)}>
                        <SelectTrigger data-testid="inspiration-target-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto (mismo tipo)</SelectItem>
                          <SelectItem value="post">Post (imagen)</SelectItem>
                          <SelectItem value="carousel">Carrusel</SelectItem>
                          <SelectItem value="reel">Reel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Include doctora reference (reels only) */}
                  {(targetMedia === "reel" || (targetMedia === "" && (openItem?.media_type === "video"))) && (
                    <div className="bg-[#FAF7F2] border border-[#E6E2DC] rounded-lg p-3 space-y-2" data-testid="inspiration-doctora-block">
                      <label className="flex items-start gap-2 text-xs text-[#1A1C1A] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeDoctora}
                          onChange={(e) => setIncludeDoctora(e.target.checked)}
                          className="accent-[#D97757] mt-0.5"
                          data-testid="inspiration-include-doctora"
                        />
                        <span className="leading-snug">
                          <span className="font-medium">Este reel me incluye a mí</span>
                          <span className="block text-[10px] text-[#5C5E5C] mt-0.5">
                            Sora usará tu foto de referencia para que tu rostro se conserve en el video.
                          </span>
                        </span>
                      </label>
                      {includeDoctora && (
                        <div className="space-y-1.5 pl-6">
                          {doctoraPhotos.length === 0 ? (
                            <p className="text-[10px] text-[#965D5D] italic">
                              Aún no subes ninguna foto de referencia. Sube una debajo.
                            </p>
                          ) : (
                            <div className="flex gap-1.5 flex-wrap">
                              {doctoraPhotos.map((p) => (
                                <div key={p.id} className="relative group">
                                  <img
                                    src={`${BACKEND}${p.url}`}
                                    alt={p.label || "Doctora"}
                                    className="w-14 h-14 rounded object-cover border-2 border-[#D97757]"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => deleteDoctoraPhoto(p.id)}
                                    className="absolute -top-1 -right-1 w-4 h-4 bg-[#965D5D] hover:bg-[#7a3f3f] text-white rounded-full text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                                    title="Eliminar"
                                  >×</button>
                                </div>
                              ))}
                            </div>
                          )}
                          <label className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase text-[#5C5E5C] cursor-pointer hover:text-[#1A1C1A]">
                            {uploadingDoctora
                              ? <><Loader2 className="w-3 h-3 animate-spin" /> Subiendo…</>
                              : <><Upload className="w-3 h-3" /> Subir foto de referencia</>}
                            <input
                              type="file"
                              accept="image/*"
                              disabled={uploadingDoctora}
                              onChange={(e) => uploadDoctoraPhoto(e.target.files?.[0])}
                              className="hidden"
                              data-testid="inspiration-upload-doctora"
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    onClick={recreate}
                    disabled={recreating}
                    className="w-full bg-[#D97757] hover:bg-[#C66749] text-white h-11 gap-2"
                    data-testid="inspiration-recreate-btn"
                  >
                    {recreating ? <><Loader2 className="w-4 h-4 animate-spin" /> {recreateStep || "Recreando…"}</> : <><Sparkles className="w-4 h-4" /> Recrear con mi estilo</>}
                  </Button>
                  {recreating && (
                    <p className="text-[10px] text-[#A69076] italic text-center leading-relaxed">
                      No cierres esta ventana. El proceso puede tardar hasta 3 min si incluye video Sora 2.
                    </p>
                  )}

                  {/* Recreations list */}
                  <div className="space-y-3 pt-2 border-t border-[#E6E2DC]">
                    <div className={TAG_CLS}>Recreaciones ({(openItem.recreations || []).length})</div>
                    {(openItem.recreations || []).length === 0 && (
                      <p className="text-xs text-[#A69076] italic">Aún ninguna. Ajusta el slider y click en Recrear.</p>
                    )}
                    {(openItem.recreations || []).slice().reverse().map((rec) => (
                      <RecreationPreview
                        key={rec.id}
                        rec={rec}
                        item={openItem}
                        itemId={openItem.id}
                        onPublish={() => publishRecreation(rec)}
                        onPublished={async () => {
                          const detail = await api.get(`/inspiration/items/${openItem.id}`);
                          setOpenItem(detail.data);
                        }}
                        onDeleted={async () => {
                          const detail = await api.get(`/inspiration/items/${openItem.id}`);
                          setOpenItem(detail.data);
                          await load();
                        }}
                        publishing={publishingId === rec.id}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// -----------------------------------------------------------------------------
// RecreationPreview — IG-style mockup + inline editing + publish
// -----------------------------------------------------------------------------
const IG_HANDLE = "dratania.mendoza";
const IG_AVATAR = "https://images.weserv.nl/?url=instagram.com/dratania.mendoza/&h=80&w=80&fit=cover&default=data:image/svg+xml,%3Csvg width%3D%2780%27 height%3D%2780%27 xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%3E%3Ccircle cx%3D%2740%27 cy%3D%2740%27 r%3D%2740%27 fill%3D%27%23D97757%27%2F%3E%3Ctext x%3D%2750%25%27 y%3D%2755%25%27 font-family%3D%27serif%27 font-size%3D%2732%27 text-anchor%3D%27middle%27 fill%3D%27white%27%3ET%3C%2Ftext%3E%3C%2Fsvg%3E";

function RecreationPreview({ rec, item, itemId, onPublish, onPublished, onDeleted, publishing }) {
  const [editing, setEditing] = useState(false);
  const [caption, setCaption] = useState(rec.caption || "");
  const [hashtags, setHashtags] = useState((rec.hashtags || []).join(", "));
  const [reelPrompt, setReelPrompt] = useState(rec.reel_prompt || "");
  const [visualScene, setVisualScene] = useState(rec.visual_scene || "");
  const [textOverlays, setTextOverlays] = useState((rec.text_overlays || []).join("\n"));
  const [imageB64, setImageB64] = useState(rec.image_base64 || "");
  const [saving, setSaving] = useState(false);
  const [rerenderingText, setRerenderingText] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);
  const [generatingVideo, setGeneratingVideo] = useState(false);

  // Hook overlay editor (posts only)
  const [hookEditor, setHookEditor] = useState(null); // { text, position }
  const [hookBusy, setHookBusy] = useState(false);
  const openHookEditor = () => setHookEditor({
    text: rec.hook_overlay?.text || item?.analysis?.hook || "",
    position: rec.hook_overlay?.position || "bottom",
    style: rec.hook_overlay?.style || "editorial",
  });
  const applyHookOverlay = async () => {
    if (!hookEditor?.text?.trim()) { toast.error("Escribe el gancho primero"); return; }
    setHookBusy(true);
    try {
      const res = await api.post(
        `/inspiration/items/${itemId}/recreations/${rec.id}/apply-hook`,
        { hook: hookEditor.text.trim(), position: hookEditor.position, style: hookEditor.style }
      );
      toast.success("Gancho aplicado sobre la imagen");
      setImageB64(res.data.image_base64);
      setHookEditor(null);
      onPublished && (await onPublished());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar");
    } finally { setHookBusy(false); }
  };
  const removeHookOverlay = async () => {
    if (!confirm("¿Quitar el texto de la imagen y volver a la versión limpia?")) return;
    setHookBusy(true);
    try {
      const res = await api.delete(
        `/inspiration/items/${itemId}/recreations/${rec.id}/hook-overlay`
      );
      if (res.data.image_base64) setImageB64(res.data.image_base64);
      toast.success("Texto removido");
      onPublished && (await onPublished());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setHookBusy(false); }
  };

  useEffect(() => {
    setCaption(rec.caption || "");
    setHashtags((rec.hashtags || []).join(", "));
    setReelPrompt(rec.reel_prompt || "");
    setVisualScene(rec.visual_scene || "");
    setTextOverlays((rec.text_overlays || []).join("\n"));
    setImageB64(rec.image_base64 || "");
  }, [rec.id, rec.caption, rec.hashtags, rec.image_base64, rec.reel_prompt, rec.visual_scene, rec.text_overlays]);

  const isCarousel = rec.media_type === "carousel";
  const isReel = rec.media_type === "reel";
  const slides = (rec.slides || []).filter((s) => s && s.image_base64);
  const currentSlide = isCarousel && slides.length > 0 ? slides[Math.min(slideIdx, slides.length - 1)] : null;
  const videoUrl = rec.video_url ? `${BACKEND}${rec.video_url}` : null;

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Máximo 8MB"); return; }
    const data = await fileToDataUrl(file);
    setImageB64(data);
  };

  const generateVideo = async () => {
    if (!confirm("Generar el video con Sora 2 puede tardar 1-3 minutos y consume créditos.\n\n¿Continuar?")) return;
    setGeneratingVideo(true);
    try {
      // Fire and forget — backend runs in background task
      await api.post(
        `/inspiration/items/${itemId}/recreations/${rec.id}/generate-video`
      );
      toast.info("Sora 2 procesando… te aviso cuando esté listo");
      // Poll until done
      const start = Date.now();
      const maxMs = 8 * 60 * 1000;
      while (Date.now() - start < maxMs) {
        await new Promise((r) => setTimeout(r, 8000));
        try {
          const r2 = await api.get(`/inspiration/items/${itemId}`);
          const updated = (r2.data?.recreations || []).find((x) => x.id === rec.id);
          if (updated?.video_status === "done") {
            toast.success("Video generado 🎬");
            onPublished && (await onPublished());
            return;
          }
          if (updated?.video_status === "failed") {
            toast.error(updated?.video_error || "Sora 2 falló");
            onPublished && (await onPublished());
            return;
          }
        } catch (_) { /* transient poll error, keep trying */ }
      }
      toast.error("Timeout esperando a Sora 2 (8 min)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error generando video");
    } finally { setGeneratingVideo(false); }
  };

  const rerenderText = async () => {
    setRerenderingText(true);
    try {
      await api.post(
        `/inspiration/items/${itemId}/recreations/${rec.id}/rerender-text`
      );
      toast.success("Textos aplicados al video ✨");
      onPublished && (await onPublished());
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error";
      if (detail.includes("crudo no encontrado") || detail.includes("crudo")) {
        toast.error("El video crudo no está disponible. Debes regenerar con Sora.");
      } else {
        toast.error(detail);
      }
    } finally { setRerenderingText(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        caption,
        hashtags: hashtags.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (imageB64 !== (rec.image_base64 || "")) body.image_base64 = imageB64;
      if (rec.media_type === "reel") {
        if (reelPrompt !== (rec.reel_prompt || "")) body.reel_prompt = reelPrompt;
        if (visualScene !== (rec.visual_scene || "")) body.visual_scene = visualScene;
        const newOverlays = textOverlays.split("\n").map((s) => s.trim()).filter(Boolean);
        const oldOverlays = rec.text_overlays || [];
        if (JSON.stringify(newOverlays) !== JSON.stringify(oldOverlays)) {
          body.text_overlays = newOverlays;
        }
      }
      await api.patch(`/inspiration/items/${itemId}/recreations/${rec.id}`, body);
      toast.success("Cambios guardados");
      setEditing(false);
      onPublished && (await onPublished());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const removeRecreation = async () => {
    if (!confirm("¿Descartar esta recreación?")) return;
    try {
      await api.delete(`/inspiration/items/${itemId}/recreations/${rec.id}`);
      toast.success("Descartada");
      onDeleted && (await onDeleted());
    } catch (err) {
      toast.error("Error eliminando");
    }
  };

  const captionForPreview = caption || "(caption vacío — genera de nuevo o edita)";
  const mediaLabel = rec.media_type === "reel" ? "REEL" : rec.media_type === "carousel" ? "CARRUSEL" : "POST";
  const published = !!rec.published_ref;

  return (
    <div
      className="bg-white border border-[#E6E2DC] rounded-xl overflow-hidden shadow-sm"
      data-testid={`inspiration-recreation-${rec.id}`}
    >
      {/* Badges */}
      <div className="flex items-center gap-1.5 flex-wrap px-3 pt-3 pb-2">
        <span className="text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#1A1C1A] text-[#F4F0EB]">
          Vista previa · {mediaLabel}
        </span>
        <span className="text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#F4F0EB] text-[#5C5E5C]">Fid {rec.fidelity}%</span>
        <span className="text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#E6F0E8] text-[#5a7a64]">{rec.service_tag}</span>
        {published && (
          <span className="text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#D97757] text-white flex items-center gap-1">
            <CheckCircle2 className="w-2.5 h-2.5" /> Enviado a {rec.published_ref.kind === "reel" ? "Reels" : "Cronograma"}
          </span>
        )}
      </div>

      {/* IG-style mockup */}
      <div className="mx-3 mb-3 bg-white border border-[#DBDBDB] rounded-md overflow-hidden">
        {/* Header row */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[#EFEFEF]">
          <img
            src={IG_AVATAR}
            alt=""
            className="w-8 h-8 rounded-full object-cover ring-1 ring-[#D97757]"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-semibold text-[#262626] leading-tight truncate">{IG_HANDLE}</div>
            <div className="text-[10px] text-[#8E8E8E] truncate">Pachuca, Hidalgo</div>
          </div>
          <Instagram className="w-4 h-4 text-[#8E8E8E]" />
        </div>

        {/* Media */}
        <div className="relative bg-[#FAFAFA]">
          {isCarousel && slides.length > 0 ? (
            <>
              <img
                src={currentSlide.image_base64}
                alt=""
                className="w-full aspect-square object-cover"
              />
              {/* Slide counter (top-right IG style) */}
              <div className="absolute top-2 right-2 bg-black/60 text-white text-[11px] font-medium px-2 py-0.5 rounded-full">
                {slideIdx + 1}/{slides.length}
              </div>
              {/* Nav arrows */}
              {slideIdx > 0 && (
                <button
                  type="button"
                  onClick={() => setSlideIdx((i) => Math.max(0, i - 1))}
                  className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center text-[#262626]"
                  aria-label="Slide anterior"
                >‹</button>
              )}
              {slideIdx < slides.length - 1 && (
                <button
                  type="button"
                  onClick={() => setSlideIdx((i) => Math.min(slides.length - 1, i + 1))}
                  className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center text-[#262626]"
                  aria-label="Slide siguiente"
                >›</button>
              )}
              {/* Dots */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSlideIdx(i)}
                    className={`w-1.5 h-1.5 rounded-full transition ${i === slideIdx ? "bg-[#3897F0]" : "bg-white/60"}`}
                    aria-label={`Ir a slide ${i + 1}`}
                  />
                ))}
              </div>
              {/* Title/body overlay tag (subtle) */}
              {(currentSlide.title || currentSlide.body) && (
                <div className="absolute bottom-8 left-2 right-2 bg-black/50 backdrop-blur-sm text-white text-[10px] px-2 py-1 rounded max-h-24 overflow-hidden">
                  {currentSlide.title && <div className="font-semibold">{currentSlide.title}</div>}
                  {currentSlide.body && <div className="opacity-80 line-clamp-2">{currentSlide.body}</div>}
                </div>
              )}
            </>
          ) : isReel && videoUrl ? (
            <video
              src={videoUrl}
              controls
              playsInline
              className="w-full aspect-[9/16] object-cover bg-black"
              data-testid={`inspiration-video-${rec.id}`}
            />
          ) : imageB64 ? (
            <>
              <img src={imageB64} alt="preview" className="w-full aspect-square object-cover" />
              {!editing && !isCarousel && !isReel && (
                <button
                  type="button"
                  onClick={openHookEditor}
                  disabled={hookBusy}
                  data-testid={`post-hook-btn-${rec.id}`}
                  className="absolute top-2 left-2 bg-white/95 hover:bg-white text-[9px] tracking-[0.2em] uppercase font-medium px-2 py-1 rounded-full shadow flex items-center gap-1 text-[#1A1C1A]"
                >
                  <Sparkles className="w-3 h-3 text-[#D97757]" />
                  {rec.hook_overlay?.text ? "Editar gancho" : "Poner gancho"}
                </button>
              )}
              {rec.hook_overlay?.text && !editing && (
                <button
                  type="button"
                  onClick={removeHookOverlay}
                  disabled={hookBusy}
                  className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white text-[9px] tracking-widest uppercase px-2 py-1 rounded-full"
                  title="Quitar gancho"
                >
                  Quitar
                </button>
              )}
            </>
          ) : isReel ? (
            <div className="aspect-[9/16] flex flex-col items-center justify-center bg-gradient-to-br from-[#1A1C1A] to-[#3a3b39] text-[#F4F0EB] p-6 text-center">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A69076] mb-2">
                Reel · {rec.video_status === "failed" ? "Video falló" : "Sin video aún"}
              </div>
              <p className="text-xs text-[#F4F0EB]/80 leading-relaxed max-w-[240px] italic">
                {reelPrompt ? `"${reelPrompt.slice(0, 180)}${reelPrompt.length > 180 ? "…" : ""}"` : "Prompt Sora 2 vacío"}
              </p>
              {rec.video_error && (
                <p className="text-[10px] text-[#E88A8A] mt-2">{rec.video_error}</p>
              )}
              {rec.video_status === "generating" || generatingVideo ? (
                <p className="text-[10px] text-[#A69076] mt-3 flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> Generando con Sora 2… (1-3 min)
                </p>
              ) : rec.video_status === "failed" ? (
                <p className="text-[10px] text-[#A69076] mt-3">Video falló. Reintenta desde el botón abajo.</p>
              ) : (
                <p className="text-[10px] text-[#A69076] mt-3">Recreación antigua sin video. Recréala de nuevo para incluir video Sora 2.</p>
              )}
            </div>
          ) : (
            <div className="aspect-square flex items-center justify-center bg-[#F4F0EB] text-[10px] text-[#A69076] tracking-widest uppercase">
              {rec.image_error ? "Imagen falló" : "Sin imagen"}
            </div>
          )}
          {editing && !isCarousel && !isReel && (
            <label className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-[10px] px-2 py-1 rounded shadow cursor-pointer flex items-center gap-1 tracking-widest uppercase text-[#1A1C1A]">
              <Upload className="w-3 h-3" /> Cambiar imagen
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          )}
        </div>

        {/* Action row */}
        <div className="flex items-center gap-3 px-3 py-2">
          <Heart className="w-5 h-5 text-[#262626]" />
          <MessageCircle className="w-5 h-5 text-[#262626]" />
          <Send className="w-5 h-5 text-[#262626] -rotate-12" />
          <div className="flex-1" />
          <Bookmark className="w-5 h-5 text-[#262626]" />
        </div>

        {/* Caption preview */}
        <div className="px-3 pb-3 space-y-1">
          <div className="text-[11px] text-[#262626] font-semibold">1,284 me gusta</div>
          {editing ? (
            <div className="space-y-2 pt-1">
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={6}
                placeholder="Escribe el caption…"
                className="text-xs resize-y"
                data-testid={`inspiration-edit-caption-${rec.id}`}
              />
              <Input
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                placeholder="hashtag1, hashtag2 (sin #)"
                className="text-[11px]"
                data-testid={`inspiration-edit-hashtags-${rec.id}`}
              />
              {rec.media_type === "reel" && (
                <>
                  <Textarea
                    value={visualScene}
                    onChange={(e) => setVisualScene(e.target.value)}
                    rows={4}
                    placeholder="Escena visual (lo que Sora debe filmar — en inglés)"
                    className="text-[11px] resize-y font-mono"
                    data-testid={`inspiration-edit-scene-${rec.id}`}
                  />
                  <Textarea
                    value={textOverlays}
                    onChange={(e) => setTextOverlays(e.target.value)}
                    rows={4}
                    placeholder="Textos en pantalla — un texto por línea (español, 2-6 palabras c/u)"
                    className="text-[11px] resize-y"
                    data-testid={`inspiration-edit-overlays-${rec.id}`}
                  />
                  <details>
                    <summary className="text-[10px] tracking-widest uppercase text-[#A69076] cursor-pointer">Prompt Sora completo (avanzado)</summary>
                    <Textarea
                      value={reelPrompt}
                      onChange={(e) => setReelPrompt(e.target.value)}
                      rows={3}
                      placeholder="Prompt Sora 2 completo…"
                      className="text-[11px] resize-y font-mono mt-1"
                    />
                  </details>
                </>
              )}
            </div>
          ) : (
            <div className="text-[12px] text-[#262626] leading-snug">
              <span className="font-semibold">{IG_HANDLE}</span>{" "}
              <span className="whitespace-pre-wrap">
                {expanded || captionForPreview.length <= 180
                  ? captionForPreview
                  : captionForPreview.slice(0, 180) + "… "}
              </span>
              {!expanded && captionForPreview.length > 180 && (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="text-[#8E8E8E] hover:text-[#262626]"
                >más</button>
              )}
              {(rec.hashtags || []).length > 0 && (
                <div className="text-[12px] text-[#00376B] mt-1 leading-snug">
                  {(rec.hashtags || []).map((h) => `#${h}`).join(" ")}
                </div>
              )}
            </div>
          )}
          <div className="text-[10px] text-[#8E8E8E] uppercase tracking-wider pt-1">hace unos segundos</div>
        </div>
      </div>

      {/* Storyboard preview (reels only) — shows the user WHAT Sora will generate */}
      {isReel && !editing && (rec.visual_scene || (rec.text_overlays || []).length > 0) && (
        <div className="mx-3 mb-3 bg-[#FAF7F2] border border-[#E6E2DC] rounded-lg p-3 space-y-2" data-testid={`inspiration-storyboard-${rec.id}`}>
          <div className="text-[9px] tracking-[0.25em] uppercase text-[#A69076]">Storyboard del reel</div>
          {rec.visual_scene && (
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#5C5E5C] mb-0.5">Escena visual (Sora 2)</div>
              <p className="text-[11px] text-[#1A1C1A] leading-relaxed">{rec.visual_scene}</p>
            </div>
          )}
          {(rec.text_overlays || []).length > 0 && (
            <div>
              <div className="text-[10px] tracking-widest uppercase text-[#5C5E5C] mb-0.5">Textos en pantalla (secuencia)</div>
              <ol className="text-[11px] text-[#1A1C1A] space-y-0.5 list-decimal ml-4">
                {(rec.text_overlays || []).map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ol>
            </div>
          )}
          <p className="text-[9px] text-[#A69076] italic pt-1">
            Sora 2 filma la escena. Los textos se queman encima en post-producción con kinetic reel.
          </p>
          {/* Rerender / regenerate buttons */}
          {!published && (rec.text_overlays || []).length > 0 && videoUrl && (
            <div className="pt-2 space-y-1.5">
              {rec.raw_video_url ? (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={rerenderText}
                    disabled={rerenderingText}
                    className="flex-1 h-9 text-[11px] border-[#D97757] text-[#D97757] hover:bg-[#D97757] hover:text-white gap-1.5"
                    data-testid={`inspiration-rerender-text-${rec.id}`}
                    title="Recompone los textos sobre el video ya generado — GRATIS y rápido (5s)"
                  >
                    {rerenderingText
                      ? <><Loader2 className="w-3 h-3 animate-spin" /> Aplicando…</>
                      : <><RefreshCw className="w-3 h-3" /> Aplicar textos al video</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={generateVideo}
                    disabled={generatingVideo}
                    className="h-9 text-[11px] text-[#5C5E5C] hover:text-[#1A1C1A] gap-1"
                    data-testid={`inspiration-regen-full-${rec.id}`}
                    title="Regenera el video con Sora 2 desde cero (2-3 min, consume créditos)"
                  >
                    {generatingVideo
                      ? <><Loader2 className="w-3 h-3 animate-spin" /> Sora…</>
                      : <><Video className="w-3 h-3" /> Regenerar con Sora</>}
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-[10px] text-[#965D5D] italic leading-snug">
                    Este video fue generado antes de guardar el crudo. Para aplicar tus cambios, regenéralo con Sora (una vez, para tener el crudo).
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={generateVideo}
                    disabled={generatingVideo}
                    className="w-full h-9 text-[11px] border-[#D97757] text-[#D97757] hover:bg-[#D97757] hover:text-white gap-1.5"
                    data-testid={`inspiration-regen-once-${rec.id}`}
                  >
                    {generatingVideo
                      ? <><Loader2 className="w-3 h-3 animate-spin" /> Sora procesando (2-3 min)…</>
                      : <><Video className="w-3 h-3" /> Regenerar video con Sora (aplica cambios)</>}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action bar */}
      <div className="px-3 pb-3 space-y-2">
        {rec.image_error && (
          <p className="text-[10px] text-[#965D5D] italic">La imagen falló al generarse: {rec.image_error}. Sube una custom o vuelve a Recrear.</p>
        )}
        {/* Reel fallback: video failed → offer retry button */}
        {isReel && !videoUrl && rec.video_status === "failed" && !editing && !published && (
          <Button
            size="sm"
            onClick={generateVideo}
            disabled={generatingVideo || !reelPrompt}
            variant="outline"
            className="w-full h-9 text-xs gap-1.5 border-[#D97757] text-[#D97757] hover:bg-[#D97757] hover:text-white"
            data-testid={`inspiration-gen-video-${rec.id}`}
          >
            {generatingVideo
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reintentando (1-3 min)…</>
              : <><Video className="w-3.5 h-3.5" /> Reintentar video Sora 2</>}
          </Button>
        )}
        {!published && (
          <div className="space-y-2">
            {editing ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={save}
                  disabled={saving}
                  className="flex-1 h-9 bg-[#1A1C1A] hover:bg-[#3a3b39] text-white gap-1.5 text-xs"
                  data-testid={`inspiration-save-edit-${rec.id}`}
                >
                  {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Guardando…</> : <><Check className="w-3 h-3" /> Guardar cambios</>}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditing(false);
                    setCaption(rec.caption || "");
                    setHashtags((rec.hashtags || []).join(", "));
                    setImageB64(rec.image_base64 || "");
                    setReelPrompt(rec.reel_prompt || "");
                    setVisualScene(rec.visual_scene || "");
                    setTextOverlays((rec.text_overlays || []).join("\n"));
                  }}
                  className="h-9 text-xs text-[#5C5E5C]"
                >
                  <X className="w-3 h-3 mr-1" /> Cancelar
                </Button>
              </div>
            ) : (
              <>
                <Button
                  onClick={onPublish}
                  disabled={publishing}
                  className="w-full h-11 bg-[#D97757] hover:bg-[#C66749] text-white gap-2 text-sm font-medium"
                  data-testid={`inspiration-publish-${rec.id}`}
                >
                  {publishing
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando…</>
                    : <><Check className="w-4 h-4" /> Aprobar y agendar publicación</>}
                </Button>
                <div className="flex items-center justify-between gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditing(true)}
                    className="h-8 text-[11px] text-[#5C5E5C] hover:text-[#1A1C1A] gap-1"
                    data-testid={`inspiration-edit-btn-${rec.id}`}
                  >
                    <Pencil className="w-3 h-3" /> Cambiar algo
                  </Button>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(caption); toast.success("Caption copiado"); }}
                      className="h-8 w-8 flex items-center justify-center rounded text-[#A69076] hover:text-[#1A1C1A] hover:bg-[#F4F0EB]"
                      title="Copiar caption"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={removeRecreation}
                      className="h-8 w-8 flex items-center justify-center rounded text-[#A69076] hover:text-[#965D5D] hover:bg-[#F4F0EB]"
                      title="Descartar recreación"
                      data-testid={`inspiration-delete-rec-${rec.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {published && (
          <p className="text-[10px] text-[#5a7a64] italic flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Publicación agendada. También aparece en {rec.published_ref.kind === "reel" ? "Admin → Reels Studio" : "Admin → Cronograma"} para gestión avanzada.
          </p>
        )}
      </div>

      {/* Hook overlay editor modal (posts only) */}
      {hookEditor && (
        <div
          className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
          data-testid={`hook-editor-modal-${rec.id}`}
          onClick={() => !hookBusy && setHookEditor(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Gancho sobre la imagen</div>
              <h3 className="font-display text-xl text-[#1A1C1A]">Escribe un texto que pare el scroll</h3>
              <p className="text-xs text-[#5C5E5C] mt-1 leading-relaxed">Se quema sobre la foto en tipografía editorial. Máx ~14 palabras.</p>
            </div>
            <Textarea
              value={hookEditor.text}
              onChange={(e) => setHookEditor({ ...hookEditor, text: e.target.value.slice(0, 140) })}
              rows={3}
              placeholder={item?.analysis?.hook || "Tu piel no te está fallando. Te está contando la verdad."}
              data-testid={`hook-text-input-${rec.id}`}
              className="text-sm"
            />
            <div className="text-[10px] text-right text-[#5C5E5C]">{hookEditor.text.length}/140</div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#A69076] mb-2">Estilo</div>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { v: "editorial", l: "Editorial", sub: "Serif · banda oscura" },
                  { v: "bold",       l: "Bold",      sub: "Impacto · banda coral" },
                  { v: "minimal",    l: "Minimal",   sub: "Sin banda · esquina" },
                ].map((s) => (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => setHookEditor({ ...hookEditor, style: s.v })}
                    className={`text-left px-2 py-2 rounded-md border transition ${
                      hookEditor.style === s.v
                        ? "bg-[#1A1C1A] text-white border-[#1A1C1A]"
                        : "bg-white text-[#1A1C1A] border-[#E6E2DC] hover:border-[#A69076]"
                    }`}
                    data-testid={`hook-style-${s.v}-${rec.id}`}
                  >
                    <div className="text-[10px] tracking-widest uppercase font-medium">{s.l}</div>
                    <div className={`text-[9px] leading-tight mt-0.5 ${hookEditor.style === s.v ? "text-white/70" : "text-[#5C5E5C]"}`}>{s.sub}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#A69076] mb-2">Posición</div>
              <div className="grid grid-cols-4 gap-1.5">
                {[
                  { v: "top", l: "Arriba" },
                  { v: "top-left", l: "Arriba ←" },
                  { v: "bottom", l: "Abajo" },
                  { v: "bottom-left", l: "Abajo ←" },
                ].map((p) => (
                  <button
                    key={p.v}
                    type="button"
                    onClick={() => setHookEditor({ ...hookEditor, position: p.v })}
                    className={`text-[10px] tracking-widest uppercase px-2 py-2 rounded-md border transition ${
                      hookEditor.position === p.v
                        ? "bg-[#1A1C1A] text-white border-[#1A1C1A]"
                        : "bg-white text-[#1A1C1A] border-[#E6E2DC] hover:border-[#A69076]"
                    }`}
                    data-testid={`hook-pos-${p.v}-${rec.id}`}
                  >
                    {p.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" size="sm" onClick={() => setHookEditor(null)} disabled={hookBusy} className="text-xs">Cancelar</Button>
              <Button
                size="sm"
                onClick={applyHookOverlay}
                disabled={hookBusy}
                data-testid={`hook-apply-btn-${rec.id}`}
                className="bg-[#A69076] hover:bg-[#1A1C1A] text-white text-xs"
              >
                {hookBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Aplicar gancho
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function InspirationCard({ item, onOpen, onDelete }) {
  const nRec = (item.recreations || []).length;
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-[#E6E2DC] hover:border-[#A69076] transition group" data-testid={`inspiration-card-${item.id}`}>
      <button type="button" onClick={onOpen} className="w-full text-left">
        {item.original_thumbnail_url ? (
          <img
            src={item.original_thumbnail_url}
            referrerPolicy="no-referrer"
            alt=""
            className="w-full aspect-square object-cover bg-[#F4F0EB]"
            onError={(e) => {
              const fallback = `${BACKEND}/api/inspiration/items/${item.id}/thumb`;
              if (e.target.src !== fallback) e.target.src = fallback;
              else e.target.src = "data:image/svg+xml,%3Csvg width='400' height='400' xmlns='http://www.w3.org/2000/svg'%3E%3Crect fill='%23F4F0EB' width='400' height='400'/%3E%3Ctext x='50%25' y='50%25' font-family='serif' font-size='14' text-anchor='middle' fill='%23A69076'%3ESin miniatura%3C/text%3E%3C/svg%3E";
            }}
          />
        ) : (
          <div className="w-full aspect-square bg-[#F4F0EB] flex items-center justify-center text-[10px] text-[#A69076] tracking-widest uppercase">
            Sin miniatura
          </div>
        )}
      </button>
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#1A1C1A] text-[#F4F0EB]">
            {item.media_type}
          </span>
          <span className="text-[10px] text-[#A69076]">
            {nRec} recreac{nRec === 1 ? "ión" : "iones"}
          </span>
        </div>
        <p className="text-xs text-[#1A1C1A] line-clamp-2 leading-snug min-h-[2.5em]">
          {item.analysis_status === "analyzing" ? (
            <span className="inline-flex items-center gap-1.5 text-[#D97757]">
              <Loader2 className="w-3 h-3 animate-spin" /> Analizando video completo…
            </span>
          ) : (item.analysis?.concept || item.original_caption?.slice(0, 100) || "(sin analisis)")}
        </p>
        <div className="flex gap-1.5">
          <Button size="sm" onClick={onOpen} className="flex-1 h-8 text-xs bg-[#D97757] hover:bg-[#C66749] text-white">
            <Wand2 className="w-3 h-3 mr-1" /> Recrear
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="h-8 w-8 p-0 text-[#A69076] hover:text-[#965D5D]">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
